import {
  ProductLineSummaryData,
  ProductLineSummaryItem,
  ProductLineSummaryTreeNode,
} from 'ts/interfaces/productLineSummary.interface'
import type { ApprovalDetail } from 'ts/interfaces/productsDetails.interface'
import { parseDealCubeHistoryByLevel } from '../../utils/parseDealCubeHistory'

interface DealCubeProductLineDetail {
  productLineCode: string
  estimatedDiscountPercentage: number
  netMarginPercentage: number
}

interface DealCubeHistoryDetail {
  level: string | number
  approversByLevel?: Array<{ decisionReasonCode: string }>
  productlineDetails: DealCubeProductLineDetail[]
}

interface ProductLineTotalsInput {
  totalNetRevenue?: number | null
  totalDiscountPercentage?: number | null
  totalOGMPercentage?: number | null
}

const normalizeOgmPercent = (value: number | null | undefined): number => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 0
  }

  return Number(value.toFixed(2))
}

const getOgmFields = (
  updatedCm: number | null | undefined,
  cm: number | null | undefined
): Pick<ProductLineSummaryTreeNode, 'ogmPercent' | 'requestedOgmPercent'> => ({
  ogmPercent: normalizeOgmPercent(updatedCm ?? cm),
  requestedOgmPercent: normalizeOgmPercent(cm),
})

const getCompositeId = (detail: DealCubeHistoryDetail): string | null => {
  const code = detail.approversByLevel?.[0]?.decisionReasonCode
  if (!code) return null
  return `${String(detail.level).trim()}-${code}`
}

const buildCategoryNode = (
  categoryItem: ProductLineSummaryItem | undefined,
  psgCode: string,
  categoryCd: string,
  categoryName: string
): ProductLineSummaryTreeNode => ({
  id: `cat-${psgCode}-${categoryCd}`,
  hierarchyPath: [psgCode, categoryName],
  hierarchyCode: categoryName,
  description: categoryName,
  netRevenue: categoryItem?.estimatedPnBdNetRevenue ?? 0,
  dataPoints: undefined,
  totalDiscountPercent: categoryItem?.estimatedDiscountPercentage || 0,
  totalDiscountPercentUpdated: categoryItem?.estimatedDiscountPercentageUpdated ?? null,
  ...getOgmFields(categoryItem?.updatedCm, categoryItem?.cm),
  updatedCm: categoryItem?.updatedCm ?? null,
  cm: categoryItem?.cm ?? null,
  expertDiscountPercentage: categoryItem?.expertDiscountPercentage ?? undefined,
  floorDiscountPercentage: categoryItem?.floorDiscountPercentage ?? undefined,
  typicalDiscountPercentage: categoryItem?.typicalDiscountPercentage ?? undefined,
  level: 1,
})

const buildProductLineNode = (
  item: ProductLineSummaryItem,
  psgCode: string,
  categoryCd: string,
  categoryName: string,
  approvalData: Record<string, ApprovalDetail>
): ProductLineSummaryTreeNode => ({
  id: `pl-${psgCode}-${categoryCd}-${item.productLineCode}`,
  hierarchyPath: [psgCode, categoryName, item.productLineCode],
  hierarchyCode: item.productLineCode,
  description: item.plDescription,
  netRevenue: item.estimatedPnBdNetRevenue ?? 0,
  dataPoints: item.dataPoints,
  totalDiscountPercent: item.estimatedDiscountPercentage || 0,
  totalDiscountPercentUpdated: item.estimatedDiscountPercentageUpdated ?? null,
  ...getOgmFields(item.updatedCm, item.cm),
  updatedCm: item.updatedCm ?? null,
  cm: item.cm ?? null,
  expertDiscountPercentage: item.expertDiscountPercentage ?? undefined,
  floorDiscountPercentage: item.floorDiscountPercentage ?? undefined,
  typicalDiscountPercentage: item.typicalDiscountPercentage ?? undefined,
  approvalData,
  level: 2,
})

export const transformProductLineSummaryData = (
  apiData: ProductLineSummaryItem[],
  dealCubeHistoryDetails: DealCubeHistoryDetail[] = [],
  totals?: ProductLineTotalsInput
): ProductLineSummaryData => {
  const emptyResult: ProductLineSummaryData = {
    items: [],
    totals: {
      netRevenue: totals?.totalNetRevenue ?? 0,
      totalDiscountPercent: totals?.totalDiscountPercentage ?? 0,
      ogmPercent: totals?.totalOGMPercentage ?? 0,
    },
  }

  if (!apiData?.length) return emptyResult

  // Single pass: build category lookup map and nested psg → category groups simultaneously
  const categoryItemsMap = new Map<string, ProductLineSummaryItem>()
  const psgGroups = new Map<string, Map<string, ProductLineSummaryItem[]>>()

  for (const item of apiData) {
    if (item.busGroupCode === '') {
      if (item.plCategoryCd) categoryItemsMap.set(item.plCategoryCd, item)
      continue
    }

    const { busGroupCode: psgCode, plCategoryCd } = item
    const categoryCd = plCategoryCd ?? ''

    if (!psgGroups.has(psgCode)) psgGroups.set(psgCode, new Map())
    const categoryGroups = psgGroups.get(psgCode)!

    if (!categoryGroups.has(categoryCd)) categoryGroups.set(categoryCd, [])
    categoryGroups.get(categoryCd)!.push(item)
  }

  const historyByApproval = parseDealCubeHistoryByLevel(
    dealCubeHistoryDetails,
    getCompositeId,
    (detail) => detail.productlineDetails,
    (lineDetail) => lineDetail.productLineCode
  )

  const treeNodes: ProductLineSummaryTreeNode[] = []

  psgGroups.forEach((categoryGroups, psgCode) => {
    treeNodes.push({
      id: `psg-${psgCode}`,
      hierarchyPath: [psgCode],
      hierarchyCode: psgCode,
      description: psgCode,
      level: 0,
    })

    categoryGroups.forEach((categoryItems, categoryCd) => {
      const categoryItem = categoryItemsMap.get(categoryCd)
      const categoryName = categoryItem?.productLineCode || categoryCd

      // Push cat node first, then pl nodes directly — no intermediate array needed
      treeNodes.push(buildCategoryNode(categoryItem, psgCode, categoryCd, categoryName))

      for (const item of categoryItems) {
        const approvalData: Record<string, ApprovalDetail> = {}
        historyByApproval.forEach((plMap, approvalId) => {
          const detail = plMap.get(item.productLineCode)
          approvalData[approvalId] = {
            ogmPercent: detail?.netMarginPercentage ?? null,
            totalDiscountPercentage: detail?.estimatedDiscountPercentage ?? null,
          }
        })

        treeNodes.push(buildProductLineNode(item, psgCode, categoryCd, categoryName, approvalData))
      }
    })
  })

  return {
    items: treeNodes,
    totals: {
      netRevenue: totals?.totalNetRevenue ?? 0,
      totalDiscountPercent: totals?.totalDiscountPercentage ?? 0,
      ogmPercent: totals?.totalOGMPercentage ?? 0,
    },
  }
}
